import React, { useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { ComponentWrapper } from '../../helperComponents/ComponentWrapper';
import { mbPlatformStyle } from '@mightybyte/rnw.utils.style-utils';
import { GAME_TYPES_TYPE, sizeTitle } from '../../../constants/constants';
import { BackButtonV2 } from '../../helperComponents/BackButtonV2';
import { ScreenProps } from '../../../typesInterfacesEnums/componentProps';
import { applyTransparency } from '../../../constants/colors';
import { useGetHiveByIdQuery } from '../MyHiveData/useGetHiveByIdQuery.gql';
import Cog from 'react-native-vector-icons/FontAwesome';
import LinearGradient from 'react-native-linear-gradient';
import ChevronDown from 'react-native-vector-icons/FontAwesome';
import { BoardSelectPopUp } from '../../helperComponents/BoardsSelectPopUp';
import { useUpdateHiveMutation } from './useUpdateHiveMutation.gql';
import HiveSettingsUserTable from '../../helperComponents/HiveSettings/HiveSettingsUserTable';
import { useEditUserFromHiveMutation } from '../MyHiveData/useEditUserFromHiveMutation.gql';
import { useHiveUsersQuery } from '../MyHiveData/useHiveUsersQuery.gql';

export default function HiveSettings({ navigation, route }: ScreenProps<'HiveSettings'>) {
  const { id } = route.params;

  const [showGamesModal, setShowGamesModal] = useState(false);
  const [selectedType, setSelectedType] = useState<'Student' | 'Class'>();
  const [selectedGames, setSelectedGames] = useState<GAME_TYPES_TYPE[] | null>(null);
  const [updateHive] = useUpdateHiveMutation();
  const [userId, setUserId] = useState<string | undefined>();
  const [editUser] = useEditUserFromHiveMutation();
  const { width } = useWindowDimensions();
  const sizeLimit = width < 800;

  const {
    data: { hive } = {},
    refetch: refetchHiveData,
    loading: hiveDataLoading,
  } = useGetHiveByIdQuery(id);

  const {
    error: getHiveUsersError,
    data: { hiveUsers: users } = {},
    refetch: refetchHiveUsers,
    loading: hiveUsersLoading,
  } = useHiveUsersQuery({
    hiveId: id,
    page: 1,
    pageSize: 2000,
  });

  // for loading
  if (!hive || hiveDataLoading) {
    return (
      <ComponentWrapper hideBee hasInset>
        <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <ActivityIndicator size={'large'} color={'E89823'} />
        </View>
      </ComponentWrapper>
    );
  }

  const allowedHiveGames = hive.allowedHiveGames;

  const handleSelectGames = async (games: GAME_TYPES_TYPE[] | null) => {
    if (selectedType === 'Class') {
      try {
        await updateHive({
          variables: { hiveId: id, data: { allowedHiveGames: games } },
        });
        refetchHiveData();
        refetchHiveUsers();
        setShowGamesModal(false);
      } catch (e) {
        console.log('Error updating hive games', e);
      }
    }
    if (selectedType === 'Student' && userId) {
      try {
        await editUser({
          variables: {
            userId,
            hiveId: id,
            allowedHiveGames: games,
          },
        });
        refetchHiveData();
        refetchHiveUsers();
        setUserId(undefined);
        setShowGamesModal(false);
      } catch (e) {
        console.log('Error editing user from hive', e);
      }
    }
  };

  const handleUserPress = ({
    userId,
    allowedHiveGames,
  }: {
    userId: string;
    allowedHiveGames?: GAME_TYPES_TYPE[] | null;
  }) => {
    setSelectedType('Student');
    setSelectedGames(allowedHiveGames ?? null);
    setShowGamesModal(true);
    setUserId(userId);
  };

  return (
    <>
      <ComponentWrapper
        hideBee
        hasInset
        scrollEnabled
        hasScroll={true}
        style={{ paddingHorizontal: 0 }}
      >
        <View style={styles.topContainer}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <BackButtonV2
              onPress={() => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate('MyHiveData', {
                    id,
                  });
                }
              }}
            />

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
              <Text style={styles.title}>
                <Cog name="cog" size={35} color="#E87030" /> Settings: {hive.name}
              </Text>
            </View>
          </View>
        </View>

        <View style={[{ marginTop: 20 }]}>
          <LinearGradient
            colors={['rgba(255, 207, 83, 0.55)', 'rgba(255, 153, 0, 0.55)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.gradientBorder}
          >
            <View style={[styles.hiveControl]}>
              <View style={[styles.hiveControlTextContainer, sizeLimit && { width: 400 }]}>
                <Text style={[styles.titleHiveControl]}> Class Boards Available</Text>
                <View>
                  <TouchableOpacity
                    onPress={() => {
                      setSelectedType('Class');
                      setSelectedGames((allowedHiveGames as GAME_TYPES_TYPE[]) || null);
                      setShowGamesModal(true);
                    }}
                    style={[styles.hiveControlButtonDropdown]}
                  >
                    <View
                      style={{ position: 'relative', flexDirection: 'row', alignItems: 'center' }}
                    >
                      <Text style={[styles.textHiveContlrolDropdown]}>
                        {allowedHiveGames?.length ? 'Limited' : 'All Boards'}
                      </Text>
                      <ChevronDown
                        name="chevron-down"
                        size={14}
                        style={[styles.dropdownArrow, sizeLimit && { right: -20 }]}
                      />
                    </View>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </LinearGradient>
        </View>
        <View style={{ marginTop: 30, width: '100%', alignItems: 'center' }}>
          <HiveSettingsUserTable
            hiveId={id}
            onPress={handleUserPress}
            users={users ?? []}
            allowedClassHiveGames={allowedHiveGames as GAME_TYPES_TYPE[]}
          />
        </View>
      </ComponentWrapper>
      <BoardSelectPopUp
        visible={showGamesModal}
        setVisible={() => setShowGamesModal(false)}
        onSavePress={handleSelectGames}
        games={selectedGames}
      />
    </>
  );
}

const styles = StyleSheet.create({
  topContainer: {
    zIndex: 100,
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    ...mbPlatformStyle({
      mobile: {
        paddingHorizontal: 20,
        marginTop: 0,
      },
      web: {
        paddingHorizontal: 60,
        marginTop: 60,
      },
    }),
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
    marginHorizontal: 25,
  },

  gradientBorder: {
    borderRadius: 16,
    padding: 2.5,
    height: 68,
  },

  hiveControl: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hiveControlTextContainer: {
    position: 'relative',
    height: 60,
    width: 787,
    justifyContent: 'space-between',
    borderRadius: 16,
    backgroundColor: '#FFF5DD',
    opacity: 0.8,
    overflow: 'hidden',
    paddingHorizontal: 27,
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleHiveControl: {
    fontFamily: 'Secular One',
    fontSize: 16,
    fontWeight: '400',
    color: '#121212',
    textWrap: 'none',
  },
  dropdownArrow: {
    position: 'absolute',
    right: -40,
    color: '#000000',
  },
  hiveControlButtonDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
    width: 230,
    paddingHorizontal: 10,
  },
  textHiveContlrolDropdown: {
    fontFamily: 'Secular One',
    fontSize: 16,
    fontWeight: '400',
    color: '#E87030',
  },
});
